import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getPagosDesglose, descargarPagosDesgloseExport } from "../api"
import { formatCurrency, formatNumber, formatFechaCorta, rangoDelMes } from "../helpers"

// Dimensiones que devuelve cada agrupación
const DIMENSIONES = {
  dia: ["fecha"],
  sucursal: ["sucursal"],
  medio: ["medio"],
  sucursal_dia: ["sucursal", "fecha"],
  sucursal_medio: ["sucursal", "medio"],
  sucursal_medio_dia: ["sucursal", "medio", "fecha"],
}
const DIM_LABEL = { fecha: "Fecha", sucursal: "Sucursal", medio: "Medio" }

const AGRUPAR_OPTS = [
  { value: "sucursal", label: "Sucursal" },
  { value: "dia", label: "Día" },
  { value: "medio", label: "Medio" },
  { value: "sucursal_dia", label: "Sucursal × Día" },
  { value: "sucursal_medio", label: "Sucursal × Medio" },
  { value: "sucursal_medio_dia", label: "Sucursal × Medio × Día" },
]

const FUENTES = [
  { value: "metodos_pago", label: "POS (ajustado)", icon: "mdi-cash-register", hint: "Todas las ventas, valor reconciliado con el total de la factura" },
  { value: "datafono", label: "Datáfono", icon: "mdi-credit-card-check-outline", hint: "Solo clientes con nuestro servicio de datáfono — valor real del terminal" },
]

const Kpi = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{ flex: "1 1 180px", minWidth: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 18px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <div style={{ width: 32, height: 32, borderRadius: 9, background: `${accent}1a`, color: accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
    </div>
    <div style={{ fontSize: 19, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>{sub}</div>}
  </div>
)
Kpi.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const DesgloseTab = ({ filtros }) => {
  const [fuente, setFuente] = useState("metodos_pago")
  const [agruparPor, setAgruparPor] = useState("sucursal")
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [exportando, setExportando] = useState(null)
  const [exportInfo, setExportInfo] = useState(null)
  const [sortKey, setSortKey] = useState("valor")
  const [sortDir, setSortDir] = useState("desc")

  const rango = useMemo(() => {
    if (!filtros) return {}
    const fecha_inicio = filtros.fecha_inicio || rangoDelMes(filtros.year, filtros.month).fecha_inicio
    const fecha_fin = filtros.fecha_fin || rangoDelMes(filtros.year, filtros.month).fecha_fin
    return { fecha_inicio, fecha_fin }
  }, [filtros])

  const cargar = useCallback(async () => {
    if (!filtros?.nitemisor) return
    setLoading(true)
    setError("")
    try {
      const { data: response } = await getPagosDesglose({
        nitemisor: filtros.nitemisor,
        agrupar_por: agruparPor,
        fuente,
        fecha_inicio: rango.fecha_inicio,
        fecha_fin: rango.fecha_fin,
        sucursal: filtros.sucursal,
      })
      if (response?.success === false) throw new Error(response.error || "El servidor reportó un error.")
      setData(response)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el desglose.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [filtros, fuente, agruparPor, rango])

  useEffect(() => { cargar() }, [cargar])

  const dims = DIMENSIONES[agruparPor] || []

  const filas = useMemo(() => {
    const rows = data?.data || []
    const arr = [...rows]
    arr.sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey]
      const na = Number(va), nb = Number(vb)
      let cmp
      if (isFinite(na) && isFinite(nb) && (sortKey === "valor" || sortKey === "transacciones" || sortKey === "ticket_promedio")) {
        cmp = na - nb
      } else {
        cmp = String(va ?? "").localeCompare(String(vb ?? ""))
      }
      return sortDir === "asc" ? cmp : -cmp
    })
    return arr
  }, [data, sortKey, sortDir])

  const resumen = data?.resumen || {}

  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"))
    else { setSortKey(key); setSortDir(key === "valor" || key === "transacciones" || key === "ticket_promedio" ? "desc" : "asc") }
  }

  const handleExport = async formato => {
    if (!filtros?.nitemisor || exportando) return
    setExportando(formato)
    setExportInfo(null)
    setError("")
    try {
      const info = await descargarPagosDesgloseExport({
        nitemisor: filtros.nitemisor,
        agrupar_por: agruparPor,
        fuente,
        fecha_inicio: rango.fecha_inicio,
        fecha_fin: rango.fecha_fin,
        sucursal: filtros.sucursal,
        formato,
      })
      setExportInfo({ formato, ...info })
      setTimeout(() => setExportInfo(null), 6000)
    } catch (err) {
      setError(err.message || "Error al exportar.")
    } finally {
      setExportando(null)
    }
  }

  const renderDim = (fila, dim) => {
    const v = fila[dim]
    if (dim === "fecha") return formatFechaCorta(v)
    return v || "—"
  }

  const SortHead = ({ label, k, align = "left" }) => (
    <th
      onClick={() => toggleSort(k)}
      style={{ padding: "10px 12px", textAlign: align, fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", userSelect: "none" }}
    >
      {label}
      {sortKey === k && <i className={`mdi mdi-arrow-${sortDir === "asc" ? "up" : "down"}`} style={{ marginLeft: 4 }} />}
    </th>
  )
  SortHead.propTypes = { label: PropTypes.string, k: PropTypes.string, align: PropTypes.string }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Controles */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16, display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16 }}>
        <div>
          <div style={labelStyle}>Fuente de datos</div>
          <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 10, padding: 3, gap: 3 }}>
            {FUENTES.map(f => {
              const activo = fuente === f.value
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFuente(f.value)}
                  title={f.hint}
                  style={{
                    border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                    background: activo ? "#fff" : "transparent",
                    color: activo ? "#0f172a" : "#64748b",
                    boxShadow: activo ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  <i className={`mdi ${f.icon}`} />
                  {f.label}
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <div style={labelStyle}>Agrupar por</div>
          <select value={agruparPor} onChange={e => setAgruparPor(e.target.value)} style={{ ...inputStyle, minWidth: 200 }}>
            {AGRUPAR_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <div style={{ marginLeft: "auto" }} className="btn-group btn-group-sm">
          <button type="button" className="btn btn-outline-success" onClick={() => handleExport("xlsx")} disabled={!!exportando || !data}>
            {exportando === "xlsx" ? <><Spinner size="sm" className="me-1" /> Generando…</> : <><i className="mdi mdi-file-excel me-1" /> Excel</>}
          </button>
          <button type="button" className="btn btn-outline-primary" onClick={() => handleExport("csv")} disabled={!!exportando || !data}>
            {exportando === "csv" ? <><Spinner size="sm" className="me-1" /> Generando…</> : <><i className="mdi mdi-file-delimited me-1" /> CSV</>}
          </button>
        </div>
      </div>

      {exportInfo && (
        <div style={{ background: "#d1fae5", border: "1px solid #a7f3d0", color: "#065f46", padding: "10px 14px", borderRadius: 8, fontSize: 13, display: "flex", alignItems: "center", gap: 10 }}>
          <i className="mdi mdi-check-circle" style={{ fontSize: 18 }} />
          <span>Descarga lista{exportInfo.filename ? ` · ${exportInfo.filename}` : ""}</span>
        </div>
      )}

      {resumen.truncado && (
        <div style={{ background: "#fef3c7", border: "1px solid #fde68a", color: "#92400e", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
          <i className="mdi mdi-alert me-1" />
          Se alcanzó el tope de 200.000 filas. Afiná el rango de fechas o los filtros para ver el total completo.
        </div>
      )}

      {/* KPIs del resumen */}
      {data && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <Kpi icon="mdi-swap-horizontal" label="Transacciones" value={formatNumber(resumen.total_transacciones)} accent="#3b82f6" />
          <Kpi icon="mdi-cash-multiple" label="Valor (neto)" value={formatCurrency(resumen.total_valor)} accent="#10b981" sub="Las notas crédito restan" />
          <Kpi icon="mdi-calculator" label="Ticket promedio" value={formatCurrency(resumen.ticket_promedio)} accent="#8b5cf6" />
          <Kpi icon="mdi-table" label="Filas" value={formatNumber(resumen.total_filas)} accent="#f59e0b"
            sub={[resumen.sucursales ? `${resumen.sucursales} suc.` : null, resumen.dias ? `${resumen.dias} días` : null, resumen.medios ? `${resumen.medios} medios` : null].filter(Boolean).join(" · ")} />
        </div>
      )}

      {/* Estados */}
      {error && (
        <div style={{ padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10, display: "flex", alignItems: "center", gap: 10 }}>
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={cargar}>Reintentar</button>
        </div>
      )}

      {loading && !data && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando desglose…</div>
        </div>
      )}

      {/* Tabla */}
      {data && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
              <i className="mdi mdi-table-large me-2 text-primary" />
              Desglose · {AGRUPAR_OPTS.find(o => o.value === agruparPor)?.label}
              <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 8, fontSize: 13 }}>({formatNumber(filas.length)} filas)</span>
            </h6>
            {loading && <Spinner size="sm" color="primary" />}
          </div>

          <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ maxHeight: 560, overflowY: "auto", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 }}>
                <thead style={{ position: "sticky", top: 0, background: "#f1f5f9", zIndex: 1 }}>
                  <tr>
                    {dims.map(d => <SortHead key={d} label={DIM_LABEL[d]} k={d} />)}
                    <SortHead label="Transacciones" k="transacciones" align="right" />
                    <SortHead label="Valor" k="valor" align="right" />
                    <SortHead label="Ticket prom." k="ticket_promedio" align="right" />
                  </tr>
                </thead>
                <tbody>
                  {filas.length === 0 && (
                    <tr><td colSpan={dims.length + 3} style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
                      Sin datos para los filtros aplicados.
                    </td></tr>
                  )}
                  {filas.map((fila, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 ? "#fafbfc" : "#fff" }}>
                      {dims.map(d => (
                        <td key={d} style={{ padding: "9px 12px", color: "#0f172a", fontWeight: d === "sucursal" ? 600 : 400 }}>
                          {d === "sucursal" && <i className="mdi mdi-store text-primary me-1" />}
                          {d === "medio" && <i className="mdi mdi-credit-card-outline me-1" style={{ color: "#8b5cf6" }} />}
                          {renderDim(fila, d)}
                        </td>
                      ))}
                      <td style={{ padding: "9px 12px", textAlign: "right", color: "#475569" }}>{formatNumber(fila.transacciones)}</td>
                      <td style={{ padding: "9px 12px", textAlign: "right", color: (Number(fila.valor) || 0) < 0 ? "#ef4444" : "#059669", fontWeight: 700 }}>{formatCurrency(fila.valor)}</td>
                      <td style={{ padding: "9px 12px", textAlign: "right", color: "#475569" }}>{formatCurrency(fila.ticket_promedio)}</td>
                    </tr>
                  ))}
                </tbody>
                {filas.length > 0 && (
                  <tfoot>
                    <tr style={{ background: "#f8fafc", fontWeight: 700, position: "sticky", bottom: 0 }}>
                      <td colSpan={dims.length} style={{ padding: "10px 12px", color: "#0f172a" }}>TOTAL</td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>{formatNumber(resumen.total_transacciones)}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", color: "#059669" }}>{formatCurrency(resumen.total_valor)}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>{formatCurrency(resumen.ticket_promedio)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

DesgloseTab.propTypes = { filtros: PropTypes.object }

const labelStyle = { fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 }
const inputStyle = { fontSize: 13, padding: "8px 10px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#fff", height: 38 }

export default DesgloseTab
